import { Component, Input } from '@angular/core';
import { Entity } from '../../../../../core/models/entity.model';
import { EntityUtils } from '../../../../../core/utils/entity.util';

export interface UboSourceOfWealth {
	name: string;
	sourceOfWealth: string;
	detail?: string;
}

const BENEFICIAL_OWNER_ROLE = 'ubo';

/**
 * The source of wealth each beneficial owner declared, shown read-only.
 *
 * Onboarding asked for the whole picture to sit in Source of Funds / Wealth, where an analyst
 * reads it. The answers themselves stay in Legal Person Information: they belong to a named
 * person, and this section has no concept of one, so copying them here would leave two records
 * to keep in step. The client answers once and this follows whatever they say there.
 */
@Component({
	selector: 'bcb-ubo-source-of-wealth',
	templateUrl: './ubo-source-of-wealth.component.html',
	styleUrls: ['./ubo-source-of-wealth.component.scss'],
})
export class UboSourceOfWealthComponent {
	@Input() set entity(entity: Entity | undefined) {
		this.beneficialOwners = this.readBeneficialOwners(entity);
	}

	beneficialOwners: Array<UboSourceOfWealth> = [];

	private readonly entityUtils = new EntityUtils();

	private readBeneficialOwners(
		entity: Entity | undefined
	): Array<UboSourceOfWealth> {
		const legalPersons = entity?.profile?.onboarding?.legal_persons ?? {};

		return Object.values<Record<string, any>>(legalPersons)
			.filter((answers) => this.isBeneficialOwner(answers))
			.map((answers) => this.toSourceOfWealth(answers))
			.filter(
				(beneficialOwner): beneficialOwner is UboSourceOfWealth =>
					!!beneficialOwner
			);
	}

	private isBeneficialOwner(answers: Record<string, any>): boolean {
		return this.entityUtils
			.answeredValues(
				answers?.[this.entityUtils.questionKeys.legal_person_type]
			)
			.includes(BENEFICIAL_OWNER_ROLE);
	}

	/**
	 * A beneficial owner who has not reached the question yet is left out rather than listed as
	 * blank: the section is a summary of what has been declared, not a checklist of what has not.
	 */
	private toSourceOfWealth(
		answers: Record<string, any>
	): UboSourceOfWealth | undefined {
		const keys = this.entityUtils.questionKeys;
		const sourceOfWealth = this.entityUtils.answeredLabel(
			answers?.[keys.source_of_wealth]
		);

		if (!sourceOfWealth) {
			return undefined;
		}

		return {
			name: this.nameOf(answers),
			sourceOfWealth,
			detail: answers?.[keys.source_of_wealth_detail] || undefined,
		};
	}

	private nameOf(answers: Record<string, any>): string {
		const keys = this.entityUtils.questionKeys;
		const name = [answers?.[keys.first_name], answers?.[keys.last_name]]
			.filter(Boolean)
			.join(' ')
			.trim();

		return (
			name || answers?.[keys.entity_name] || 'Unnamed beneficial owner'
		);
	}
}
